import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useTheme,
  useMediaQuery,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SEOHead from "../components/SEOHead";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
}));

const Services = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const servicesStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "施術メニュー",
    description: "接骨施術と鍼灸施術の詳細メニュー。五十肩・腰痛施術に特化。",
    url: "https://wakabaseto.jp/services",
    mainEntity: {
      "@type": "OfferCatalog",
      name: "施術メニュー",
      description: "わかば接骨院の施術メニュー",
      itemListElement: [
        {
          "@type": "Offer",
          name: "接骨施術",
          description:
            "骨折・脱臼・捻挫・打撲・腰痛・肩の痛み・五十肩・スポーツ外傷の施術",
          itemOffered: {
            "@type": "Service",
            name: "接骨施術",
            description:
              "骨折・脱臼・捻挫・打撲・腰痛・肩の痛み・五十肩・スポーツ外傷の施術",
            provider: {
              "@type": "MedicalBusiness",
              name: "わかば接骨院",
            },
          },
        },
        {
          "@type": "Offer",
          name: "鍼灸施術",
          description:
            "神経痛・腰痛症・頸腕症候群・五十肩・頸椎捻挫後遺症の施術",
          itemOffered: {
            "@type": "Service",
            name: "鍼灸施術",
            description:
              "神経痛・腰痛症・頸腕症候群・五十肩・頸椎捻挫後遺症の施術",
            provider: {
              "@type": "MedicalBusiness",
              name: "わかば接骨院",
            },
          },
        },
      ],
    },
  };

  return (
    <>
      <SEOHead
        title="施術メニュー"
        description="愛知県瀬戸市のわかば接骨院の施術メニュー。腰の痛み・肩の痛みの施術、運動療法に特化。接骨施術と鍼灸施術の詳細をご案内。"
        keywords="施術メニュー,瀬戸市接骨院,瀬戸市整体院,瀬戸市腰痛施術,瀬戸市五十肩施術,瀬戸市運動療法,愛知県瀬戸市接骨院,愛知県瀬戸市整体院,愛知県瀬戸市腰痛,愛知県瀬戸市五十肩,愛知県瀬戸市運動療法,瀬戸市整体院検索,瀬戸市整体院比較"
        canonical="/services"
        structuredData={servicesStructuredData}
      />
      <Box>
        {/* Hero Section */}
        <Box
          sx={{
            bgcolor: "#E8F0E8",
            py: { xs: 6, sm: 8, md: 10 },
            mb: { xs: 3, sm: 4, md: 6 },
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant={isMobile ? "h2" : "h1"}
              gutterBottom
              align="center"
              sx={{
                fontSize: {
                  xs: "2rem",
                  sm: isTablet ? "2.25rem" : "2.5rem",
                  md: "3rem",
                },
              }}
            >
              愛知県瀬戸市の施術メニュー
            </Typography>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              align="center"
              sx={{
                mt: { xs: 1, md: 2 },
                color: "text.primary",
                fontSize: isTablet ? "1.1rem" : undefined,
              }}
            >
              腰の痛み・肩の痛みの施術、運動療法に特化
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="lg">
          {/* Treatment Options */}
          <Box sx={{ mb: { xs: 4, md: 8 } }}>
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h2"
              gutterBottom
              align="center"
              sx={{ fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" } }}
            >
              主な施術内容
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: { xs: 2, md: 4 },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <StyledPaper elevation={3}>
                  <LocalHospitalIcon
                    sx={{
                      fontSize: { xs: 40, md: 60 },
                      color: "primary.main",
                      mb: { xs: 1, md: 2 },
                    }}
                  />
                  <Typography
                    variant={isMobile ? "h6" : "h5"}
                    component="h2"
                    gutterBottom
                  >
                    骨折・脱臼
                  </Typography>
                  <Typography variant="body1">
                    骨折や脱臼の施術を行います。
                    <br />
                    最新の施術法を提供し、
                    <br />
                    早期回復を目指します。
                  </Typography>
                </StyledPaper>
              </Box>
              <Box sx={{ flex: 1 }}>
                <StyledPaper elevation={3}>
                  <LocalHospitalIcon
                    sx={{
                      fontSize: { xs: 40, md: 60 },
                      color: "primary.main",
                      mb: { xs: 1, md: 2 },
                    }}
                  />
                  <Typography
                    variant={isMobile ? "h6" : "h5"}
                    component="h2"
                    gutterBottom
                  >
                    捻挫・打撲
                  </Typography>
                  <Typography variant="body1">
                    捻挫や打撲の施術を行います。
                    <br />
                    痛みの軽減と機能回復を
                    <br />
                    サポートします。
                  </Typography>
                </StyledPaper>
              </Box>
              <Box sx={{ flex: 1 }}>
                <StyledPaper elevation={3}>
                  <LocalHospitalIcon
                    sx={{
                      fontSize: { xs: 40, md: 60 },
                      color: "primary.main",
                      mb: { xs: 1, md: 2 },
                    }}
                  />
                  <Typography
                    variant={isMobile ? "h6" : "h5"}
                    component="h2"
                    gutterBottom
                  >
                    腰痛・肩の痛み
                  </Typography>
                  <Typography variant="body1">
                    腰痛や肩の痛みの施術を行います。
                    <br />
                    日常生活の質の向上を
                    <br />
                    サポートします。
                  </Typography>
                </StyledPaper>
              </Box>
            </Box>
          </Box>

          {/* Insurance Information */}
          <Box
            sx={{
              mb: { xs: 4, md: 8 },
              bgcolor: "#F5F5F4",
              py: { xs: 3, md: 4 },
            }}
          >
            <Container maxWidth="lg">
              <Typography
                variant={isMobile ? "h5" : "h4"}
                component="h2"
                gutterBottom
                align="center"
                sx={{ mb: { xs: 2, md: 3 } }}
              >
                保険について
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: { xs: 2, md: 4 },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Paper
                    elevation={3}
                    sx={{ p: { xs: 2, md: 4 }, height: "100%" }}
                  >
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      gutterBottom
                    >
                      健康保険
                    </Typography>
                    <Typography variant="body1">
                      各種健康保険に対応しています。
                      <br />
                      詳しくはお問い合わせください。
                    </Typography>
                  </Paper>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Paper
                    elevation={3}
                    sx={{ p: { xs: 2, md: 4 }, height: "100%" }}
                  >
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      gutterBottom
                    >
                      自費診療
                    </Typography>
                    <Typography variant="body1">
                      保険適用外の施術も
                      <br />
                      ご相談ください。
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            </Container>
          </Box>

          {/* Treatment Menu */}
          <Box sx={{ mb: { xs: 4, md: 8 } }}>
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h2"
              gutterBottom
              align="center"
              sx={{ fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" } }}
            >
              施術メニュー
            </Typography>

            {/* Self-pay Treatments */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                gutterBottom
                sx={{
                  color: "primary.dark",
                  borderBottom: "2px solid",
                  borderColor: "primary.main",
                  pb: 1,
                  mb: 2,
                }}
              >
                実費施術メニュー
              </Typography>
              <Paper elevation={3} sx={{ overflow: "hidden" }}>
                <Box sx={{ overflowX: "auto" }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "primary.light" }}>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            施術内容
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            内容
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            料金
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell component="th" scope="row">
                            全身調整
                          </TableCell>
                          <TableCell>
                            背骨の歪みを整え、全身のバランスを改善
                          </TableCell>
                          <TableCell>¥5,000〜</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row">
                            鍼灸施術
                          </TableCell>
                          <TableCell>痛みの軽減と体の機能向上</TableCell>
                          <TableCell>¥3,000〜</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell component="th" scope="row">
                            姿勢矯正
                          </TableCell>
                          <TableCell>姿勢の歪みを矯正し、痛みを軽減</TableCell>
                          <TableCell>¥4,500〜</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row">
                            リハビリ指導
                          </TableCell>
                          <TableCell>自宅でできる運動療法の指導</TableCell>
                          <TableCell>¥3,500〜</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                ※料金は税込表示です。施術内容や時間により変動する場合があります。
                <br />
                ※初診時は別途初診料がかかります。
              </Typography>
            </Box>

            {/* Insurance-covered Treatments */}
            <Box>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                gutterBottom
                sx={{
                  color: "primary.dark",
                  borderBottom: "2px solid",
                  borderColor: "primary.main",
                  pb: 1,
                  mb: 2,
                }}
              >
                健康保険適用メニュー
              </Typography>

              {/* 接骨メニュー */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  gutterBottom
                  sx={{
                    color: "primary.dark",
                    mb: 1,
                  }}
                >
                  接骨メニュー
                </Typography>
                <Paper elevation={3} sx={{ overflow: "hidden" }}>
                  <Box sx={{ overflowX: "auto" }}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: "primary.light" }}>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              施術内容
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              内容
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              料金
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              骨折・脱臼
                            </TableCell>
                            <TableCell>骨折や脱臼の整復と固定</TableCell>
                            <TableCell>保険適用</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              捻挫・打撲
                            </TableCell>
                            <TableCell>捻挫や打撲の施術</TableCell>
                            <TableCell>保険適用</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              腰の痛み・肩の痛み（外傷）
                            </TableCell>
                            <TableCell>腰の痛みや肩の痛みの施術</TableCell>
                            <TableCell>保険適用</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              スポーツ外傷
                            </TableCell>
                            <TableCell>スポーツによる怪我の施術</TableCell>
                            <TableCell>保険適用</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Paper>
              </Box>

              {/* 鍼灸メニュー */}
              <Box>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  gutterBottom
                  sx={{
                    color: "primary.dark",
                    mb: 1,
                  }}
                >
                  鍼灸メニュー
                </Typography>
                <Paper elevation={3} sx={{ overflow: "hidden" }}>
                  <Box sx={{ overflowX: "auto" }}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: "primary.light" }}>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              施術内容
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              内容
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              料金
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              神経痛
                            </TableCell>
                            <TableCell>
                              坐骨神経痛、肋間神経痛などの施術
                            </TableCell>
                            <TableCell>保険適用</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              腰痛症
                            </TableCell>
                            <TableCell>腰痛症の鍼灸施術</TableCell>
                            <TableCell>保険適用</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              頸腕症候群
                            </TableCell>
                            <TableCell>頸腕症候群の鍼灸施術</TableCell>
                            <TableCell>保険適用</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              五十肩
                            </TableCell>
                            <TableCell>五十肩の鍼灸施術</TableCell>
                            <TableCell>保険適用</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              頸椎捻挫後遺症
                            </TableCell>
                            <TableCell>頸椎捻挫後遺症の鍼灸施術</TableCell>
                            <TableCell>保険適用</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Paper>
              </Box>
            </Box>
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: { xs: 2.5, md: 3 },
                bgcolor: "#F5F5F4",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                ※健康保険の適用可否は、負傷の原因や状態などにより異なります。
                骨折・脱臼の応急手当後の施術や、鍼灸施術で健康保険を利用する場合は、
                医師の同意が必要です。詳しくはお問い合わせください。
              </Typography>
            </Paper>
          </Box>

          {/* Treatment Process */}
          <Box sx={{ mb: { xs: 3, md: 4 } }}>
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h2"
              gutterBottom
              align="center"
              sx={{ fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" } }}
            >
              施術の流れ
            </Typography>
            <List
              sx={{
                p: { xs: 1, md: 2 },
                "& .MuiListItem-root": {
                  py: { xs: 1, md: 1.5 },
                },
              }}
            >
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon
                    color="primary"
                    sx={{ fontSize: { xs: 24, md: 28 } }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="1. 問診"
                  secondary="症状や経過を詳しくお伺いします"
                  primaryTypographyProps={{
                    variant: isMobile ? "subtitle1" : "h6",
                    sx: { mb: { xs: 0.5, md: 1 } },
                  }}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon
                    color="primary"
                    sx={{ fontSize: { xs: 24, md: 28 } }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="2. 検査"
                  secondary="症状に応じた検査を行います"
                  primaryTypographyProps={{
                    variant: isMobile ? "subtitle1" : "h6",
                    sx: { mb: { xs: 0.5, md: 1 } },
                  }}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon
                    color="primary"
                    sx={{ fontSize: { xs: 24, md: 28 } }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="3. 施術方針の説明"
                  secondary="検査結果に基づき、施術方針を説明します"
                  primaryTypographyProps={{
                    variant: isMobile ? "subtitle1" : "h6",
                    sx: { mb: { xs: 0.5, md: 1 } },
                  }}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon
                    color="primary"
                    sx={{ fontSize: { xs: 24, md: 28 } }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="4. 施術"
                  secondary="患者様に最適な施術を行います"
                  primaryTypographyProps={{
                    variant: isMobile ? "subtitle1" : "h6",
                    sx: { mb: { xs: 0.5, md: 1 } },
                  }}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon
                    color="primary"
                    sx={{ fontSize: { xs: 24, md: 28 } }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="5. 経過観察"
                  secondary="施術効果を確認し、必要に応じて施術を調整します"
                  primaryTypographyProps={{
                    variant: isMobile ? "subtitle1" : "h6",
                    sx: { mb: { xs: 0.5, md: 1 } },
                  }}
                />
              </ListItem>
            </List>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Services;
